// src/pages/TermsConditionsPage.js
import React from 'react';
import { motion } from 'framer-motion';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-2xl font-bold text-gray-800 mb-3 border-l-4 border-blue-600 pl-4">{title}</h2>
    <div className="text-gray-600 leading-relaxed space-y-3 pl-4">{children}</div>
  </div>
);

function TermsConditionsPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-sky text-ink py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            className="text-3xl sm:text-5xl font-extrabold mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Terms &amp; Conditions
          </motion.h1>
          <motion.p
            className="text-slate-500 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Last updated: April 13, 2026
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <p className="text-gray-600 mb-10 leading-relaxed">
            Welcome to <strong>Kalyani Consultancy</strong>. By accessing or using our website and services, you agree to be bound by these Terms &amp; Conditions. Please read them carefully before using our platform. If you do not agree to these terms, please discontinue use of our website.
          </p>

          <Section title="1. Acceptance of Terms">
            <p>
              By visiting our website or engaging our financial consultancy services, you confirm that you are at least 18 years of age and have the legal capacity to enter into binding agreements. These terms apply to all visitors, users, and clients of Kalyani Consultancy.
            </p>
          </Section>

          <Section title="2. Nature of Services">
            <p>
              Kalyani Consultancy is a registered financial consultancy offering services in:
            </p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Life Insurance advisory and policy facilitation</li>
              <li>Health Insurance advisory and policy facilitation</li>
              <li>Mutual Fund investments and portfolio guidance</li>
              <li>General financial and policy advisory</li>
            </ul>
            <p className="mt-3">
              Our consultants provide guidance based on individual financial goals. All investment and insurance decisions remain the client's own responsibility.
            </p>
          </Section>

          <Section title="3. Regulatory Disclaimer">
            <p>
              Kalyani Consultancy operates in compliance with applicable Indian financial regulations including those set by <strong>IRDAI</strong> (Insurance Regulatory and Development Authority of India), <strong>SEBI</strong> (Securities and Exchange Board of India), and <strong>AMFI</strong> (Association of Mutual Funds in India).
            </p>
            <p>
              <strong>Mutual fund investments are subject to market risks. Please read all scheme-related documents carefully before investing.</strong> Past performance is not indicative of future results.
            </p>
          </Section>

          <Section title="4. Use of Website">
            <p>You agree to use our website only for lawful purposes. You must not:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Use the website in a way that violates any applicable local, national, or international law or regulation.</li>
              <li>Transmit any unsolicited or unauthorised advertising or promotional material.</li>
              <li>Attempt to gain unauthorised access to our servers, systems, or data.</li>
              <li>Reproduce, duplicate, or resell any part of our website content without prior written consent.</li>
            </ul>
          </Section>

          <Section title="5. Accuracy of Information">
            <p>
              We make every effort to ensure the information on our website is accurate and up to date. However, we do not warrant that the content is free from errors or omissions. The information provided is for general informational purposes only and does not constitute financial, legal, or investment advice.
            </p>
            <p>
              Please consult our advisors directly for personalised guidance suited to your circumstances.
            </p>
          </Section>

          <Section title="6. Premium Payments">
            <p>
              Any premium payment facilitated through our website is processed via authorised payment gateways. Kalyani Consultancy does not store your payment card details. In case of disputes or failed transactions, please contact us within 7 working days.
            </p>
          </Section>

          <Section title="7. Intellectual Property">
            <p>
              All content on this website — including but not limited to text, graphics, logos, images, and software — is the property of Kalyani Consultancy or its content suppliers and is protected under applicable Indian intellectual property laws. Unauthorised use is strictly prohibited.
            </p>
          </Section>

          <Section title="8. Limitation of Liability">
            <p>
              To the fullest extent permitted by law, Kalyani Consultancy shall not be liable for any direct, indirect, incidental, or consequential loss arising from:
            </p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Reliance on information available on our website.</li>
              <li>Market fluctuations affecting investment returns.</li>
              <li>Technical errors, downtime, or unavailability of the website.</li>
              <li>Actions of third-party insurers or fund houses.</li>
            </ul>
          </Section>

          <Section title="9. Links to Third-Party Sites">
            <p>
              Our website may contain links to external websites for your convenience. These links do not imply endorsement of the content therein. Kalyani Consultancy has no control over and accepts no responsibility for the content, privacy practices, or availability of third-party websites.
            </p>
          </Section>

          <Section title="10. Termination">
            <p>
              We reserve the right to suspend or terminate access to our website or services at our discretion, without notice, in cases of suspected misuse, breach of these terms, or for operational reasons.
            </p>
          </Section>

          <Section title="11. Governing Law &amp; Jurisdiction">
            <p>
              These Terms &amp; Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising from these terms or your use of our services shall be subject to the exclusive jurisdiction of the courts at <strong>Haldia, West Bengal</strong>.
            </p>
          </Section>

          <Section title="12. Changes to These Terms">
            <p>
              Kalyani Consultancy reserves the right to modify these Terms &amp; Conditions at any time. Updates will be reflected on this page with a revised date. Continued use of our website after changes constitutes acceptance of the revised terms.
            </p>
          </Section>

          <Section title="13. Contact Us">
            <p>For questions about these Terms &amp; Conditions, please contact us:</p>
            <ul className="mt-3 space-y-1">
              <li><strong>Kalyani Consultancy</strong></li>
              <li>5th Floor, Akash Ganga Complex, Basudevpur, Haldia, West Bengal 721602</li>
              <li>📞 <a href="tel:+919476168969" className="text-blue-600 hover:underline">+91 94761 68969</a></li>
              <li>✉️ <a href="mailto:kalyaniconsultancy18@gmail.com" className="text-blue-600 hover:underline">kalyaniconsultancy18@gmail.com</a></li>
            </ul>
          </Section>
        </motion.div>
      </section>
    </>
  );
}

export default TermsConditionsPage;
